import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useFormik } from "formik";
import * as yup from "yup";
import { useEffect, useState } from "react";
import Loading from "../../../../../components/loading/loading";
import {
  addFormSectionRequest,
  deleteFormSectionRequest,
  updateFormSectionRequest,
} from "../../../../../utils/APICalls";
import ErrorNotification from "../../../../../components/error-notification/error-notification";
import { setElementUpdated } from "../../../../../utils/SingletonStore";

interface FormValues {
  isActive: boolean;
  title: string;
  introductionText?: string;
}

const ValidationSchema = yup.object({
  title: yup.string().required("This field can't be empty"),
});

export default function SectionSettings({
  data,
  templateId,
  isClient,
  onSave,
  onCancel,
  onDelete,
}: any) {
  const [loading, setLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (data) {
      formik.setFieldValue("isActive", data?.isActive);
      formik.setFieldValue("title", data?.title);
      formik.setFieldValue("introductionText", data?.introductionText);
    }
  }, [data]); // eslint-disable-line

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      isActive: data?.isActive ?? true,
      title: data?.title ?? "",
      introductionText: data?.introductionText ?? "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      if (data?.id) {
        const payload = { ...values };
        updateSection(payload);
      } else {
        const payload = { ...values, order: data?.order };
        addSection(payload);
      }
    },
  });

  const onDeleteClick = async () => {
    if (data?.id) {
      setLoading(true);
      const response = await deleteFormSectionRequest(
        templateId,
        data?.id,
        isClient,
      );
      setLoading(false);
      if (response?.status === 204) {
        onDelete(data);
      } else if (response?.status === 400) {
        setShowErrorNotification(true);
        setErrorMessage(response?.data?.detail);
      }
    } else {
      onDelete(data);
    }
  };

  const addSection = async (payload: any) => {
    setLoading(true);
    const response = await addFormSectionRequest(templateId, payload, isClient);
    setLoading(false);
    if (response?.status === 201) {
      const section = {
        id: response?.data?.id,
        title: response?.data?.title,
        order: response?.data?.order,
        introductionText: response?.data?.introductionText,
        isActive: response?.data?.isActive,
        elements: response?.data?.elements,
      };
      onSave(section);
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const updateSection = async (payload: any) => {
    setLoading(true);
    const response = await updateFormSectionRequest(
      templateId,
      data?.id,
      payload,
      isClient,
    );
    setLoading(false);
    if (response?.status === 204) {
      const section = {
        id: data?.id,
        title: payload.title,
        order: data?.order,
        introductionText: payload.introductionText,
        isActive: payload.isActive,
        elements: data?.elements,
      };
      onSave(section);
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <form onSubmit={formik.handleSubmit}>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography fontWeight={"bold"} fontSize={20}>
            Section Settings
          </Typography>
          <FormControlLabel
            control={
              <Switch
                name="isActive"
                checked={formik.values.isActive}
                onChange={(e) => {
                  formik.handleChange(e);
                  setElementUpdated(true);
                }}
              />
            }
            label={formik.values.isActive ? "Active" : "Inactive"}
          />
        </Box>
        <Box sx={{ display: "flex", my: 2 }}>
          <Typography
            sx={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.6)", width: 150 }}
          >
            Title
          </Typography>
          <TextField
            sx={{ ml: 2 }}
            fullWidth
            id="title"
            name="title"
            variant="outlined"
            value={formik.values.title}
            onChange={(e) => {
              formik.handleChange(e);
              setElementUpdated(true);
            }}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
          />
        </Box>
        <Divider />
        <Box sx={{ display: "flex", my: 2 }}>
          <Typography
            sx={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.6)", width: 150 }}
          >
            Introduction Text
          </Typography>
          <TextField
            sx={{ ml: 2 }}
            fullWidth
            id="introductionText"
            name="introductionText"
            variant="outlined"
            value={formik.values.introductionText}
            onChange={(e) => {
              formik.handleChange(e);
              setElementUpdated(true);
            }}
            helperText="Leave blank to exclude."
          />
        </Box>
        <Divider />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            my: 3,
          }}
        >
          <Button
            startIcon={<DeleteOutlinedIcon color="error" />}
            variant="text"
            color="error"
            onClick={() => {
              setElementUpdated(false);
              onDeleteClick();
            }}
          >
            Delete Section
          </Button>
          <Box>
            <Button
              variant="outlined"
              sx={{ mr: 2 }}
              onClick={() => {
                setElementUpdated(false);
                onCancel();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setElementUpdated(false);
                formik.handleSubmit();
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </form>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {loading && <Loading />}
    </Box>
  );
}
